import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';

// Dynamic Soundwave Bars Component
const ModalVoiceSoundwave = ({ isActive, color = 'emerald' }) => {
  const colorMap = {
    amber: 'bg-amber-400',
    emerald: 'bg-emerald-400',
    indigo: 'bg-indigo-400'
  };
  const barClass = colorMap[color] || colorMap.emerald;

  return (
    <div className="flex items-center justify-center gap-1.5 h-10 my-2">
      {[40, 75, 100, 60, 90, 50, 85, 45, 95, 65, 80, 55].map((heightPct, idx) => (
        <div
          key={idx}
          className={`w-1.5 rounded-full transition-all duration-200 ${barClass} ${
            isActive ? 'animate-pulse' : 'opacity-30 h-2'
          }`}
          style={{
            height: isActive ? `${Math.max(10, heightPct)}%` : '6px',
            animationDelay: `${idx * 0.08}s`,
            animationDuration: `${0.6 + (idx % 4) * 0.2}s`
          }}
        />
      ))}
    </div>
  );
};

const CandidateHrVoiceScreeningModal = ({
  isOpen,
  applicationId,
  initialData = null,
  autoStart = false,
  onClose,
  onCompleted
}) => {
  // Screens: 'intro' | 'interview' | 'evaluating' | 'completed' | 'expired'
  const [screen, setScreen] = useState('intro');
  // Conversational state: 'starting' | 'ai_speaking' | 'listening' | 'processing_answer' | 'generating_next_question'
  const [voiceStatus, setVoiceStatus] = useState('starting');
  const [applicantInfo, setApplicantInfo] = useState(initialData || null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionObj, setCurrentQuestionObj] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [capturedAnswers, setCapturedAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const autoStartedRef = useRef(false);

  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const currentAnswerRef = useRef('');
  const lastSpeechTimeRef = useRef(Date.now());
  const hasSpokenRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const maxSilenceTimerRef = useRef(null);
  const isSpeechEndedRef = useRef(false);
  const handleCandidateFinishedSpeakingRef = useRef(null);

  const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';

  const cleanPosition = (pos) => {
    if (!pos) return 'Role';
    return String(pos).replace(/\s*Interview\s*$/i, '').trim();
  };

  // Fetch HR questionnaire setup and 7 core questions
  const fetchSetup = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await fetch(`${API_BASE}/job-applications/${applicationId}/hr-screening-questionnaire`);
      const json = await res.json();
      if (json?.success && json.data) {
        const d = json.data;
        setApplicantInfo(d);
        if (typeof d.remaining_seconds === 'number') {
          setRemainingSeconds(d.remaining_seconds);
        }
        if (d.is_expired || d.hr_screening_status === 'EXPIRED') {
          setScreen('expired');
        } else if (d.hr_screening_score !== null && d.hr_screening_score !== undefined) {
          setResult({
            requirement_match_score: d.hr_screening_score,
            hr_screening_status: d.hr_screening_status,
            is_passed: d.hr_screening_score >= 70,
            evaluation: d.saved_answers?.requirements_eval || d.saved_answers?.evaluation || []
          });
          setScreen('completed');
        }

        if (d.questions && Array.isArray(d.questions)) {
          setQuestions(d.questions);
        }
      }
    } catch (e) {
      console.log('HR Screening setup fetch error:', e.message);
    }
  }, [applicationId, API_BASE]);

  useEffect(() => {
    if (isOpen) {
      fetchSetup();
    }
  }, [isOpen, fetchSetup]);

  // Handle autoStart trigger when transition occurs from AI Screening
  useEffect(() => {
    if (isOpen && autoStart && screen === 'intro' && !autoStartedRef.current) {
      autoStartedRef.current = true;
      const t = setTimeout(() => {
        handleStartHrInterview();
      }, 400);
      return () => clearTimeout(t);
    }
  }, [isOpen, autoStart, screen]);

  // Teardown microphone
  const stopListening = useCallback(() => {
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (maxSilenceTimerRef.current) {
      clearTimeout(maxSilenceTimerRef.current);
      maxSilenceTimerRef.current = null;
    }
    isListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onstart = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
      } catch (e) {}
      recognitionRef.current = null;
    }
  }, []);

  // Text-To-Speech (TTS): AI Speaks Question with strict turn isolation
  const speakAI = useCallback((text) => {
    return new Promise((resolve) => {
      stopListening();
      setLiveTranscript('');
      setVoiceStatus('ai_speaking');

      if (!window.speechSynthesis) return resolve();

      try {
        window.speechSynthesis.cancel();
        window.speechSynthesis.resume();
      } catch (e) {}

      let isResolved = false;
      const safeResolve = () => {
        if (!isResolved) {
          isResolved = true;
          setTimeout(resolve, 400);
        }
      };

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.98;
      utterance.pitch = 1.0;
      utterance.lang = 'en-US';

      utterance.onend = safeResolve;
      utterance.onerror = safeResolve;

      const words = text.split(/\s+/).length;
      const expectedAudioMs = (words / 2.5) * 1000 + 1500;
      const safetyTimeout = setTimeout(safeResolve, expectedAudioMs);

      const oldOnEnd = utterance.onend;
      utterance.onend = (e) => {
        clearTimeout(safetyTimeout);
        if (oldOnEnd) oldOnEnd(e);
      };

      window.speechSynthesis.speak(utterance);
    });
  }, [stopListening]);

  // Speech-To-Text (STT): Start Listening for Candidate Spoken Answer
  const startContinuousListening = useCallback(() => {
    stopListening();

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Browser Speech Recognition not supported. Please use Chrome/Edge.');
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognitionRef.current = recognition;

      currentAnswerRef.current = '';
      lastSpeechTimeRef.current = Date.now();
      hasSpokenRef.current = false;
      isSpeechEndedRef.current = false;

      setLiveTranscript('');
      setVoiceStatus('listening');
      isListeningRef.current = true;

      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript + ' ';
        }
        const cleanT = transcript.trim();

        if (cleanT.length > 0) {
          currentAnswerRef.current = cleanT;
          setLiveTranscript(cleanT);
          lastSpeechTimeRef.current = Date.now();
          hasSpokenRef.current = true;
        }
      };

      recognition.onerror = (e) => {
        if (e.error === 'no-speech' || e.error === 'aborted') return;
        console.log('STT error:', e.error);
      };

      recognition.onend = () => {
        if (isListeningRef.current && !isSpeechEndedRef.current) {
          try {
            recognition.start();
          } catch (e) {}
        }
      };

      recognition.start();

      // 3.0s Silence Detector
      silenceTimerRef.current = setInterval(() => {
        if (!isListeningRef.current) return;
        const silentMs = Date.now() - lastSpeechTimeRef.current;
        if (hasSpokenRef.current && silentMs >= 3000 && !isSpeechEndedRef.current) {
          isSpeechEndedRef.current = true;
          stopListening();
          if (handleCandidateFinishedSpeakingRef.current) {
            handleCandidateFinishedSpeakingRef.current();
          }
        }
      }, 500);

      // 35s Max Silence Auto-Advance
      maxSilenceTimerRef.current = setTimeout(() => {
        if (isListeningRef.current && !isSpeechEndedRef.current) {
          isSpeechEndedRef.current = true;
          stopListening();
          if (handleCandidateFinishedSpeakingRef.current) {
            handleCandidateFinishedSpeakingRef.current();
          }
        }
      }, 35000);
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
    }
  }, [stopListening]);

  // Submit HR voice answers to backend
  const handleSubmitHrVoiceAnswers = useCallback(async (finalAnswersMap) => {
    setScreen('evaluating');
    try {
      const res = await fetch(`${API_BASE}/job-applications/${applicationId}/submit-hr-screening`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: finalAnswersMap })
      });
      const json = await res.json();
      if (json?.success && json.data) {
        setResult(json.data);
        if (onCompleted) onCompleted(json.data);
      } else {
        throw new Error(json?.error || 'Failed to submit HR screening responses.');
      }
    } catch (e) {
      console.error('HR Screening submission error:', e);
      setError(e.message);
    } finally {
      setScreen('completed');
    }
  }, [applicationId, API_BASE, onCompleted]);

  // Advance to next question or submit when candidate finishes speaking
  const handleCandidateFinishedSpeaking = useCallback(async () => {
    setVoiceStatus('processing_answer');
    const qKey = currentQuestionObj?.id || `req_${questionIndex + 1}`;
    const ansText = currentAnswerRef.current.trim() || 'Candidate submitted spoken answer';

    const updatedAnswers = {
      ...capturedAnswers,
      [qKey]: ansText
    };
    setCapturedAnswers(updatedAnswers);

    const nextIndex = questionIndex + 1;
    setQuestionIndex(nextIndex);

    if (nextIndex >= questions.length) {
      // Finished all questions
      setVoiceStatus('ai_speaking');
      const cName = applicantInfo?.applicant_name || 'Candidate';
      const closing = `Thank you, ${cName}. That completes your HR Screening interview. We are now evaluating your requirements and responses.`;
      await speakAI(closing);
      await handleSubmitHrVoiceAnswers(updatedAnswers);
    } else {
      const nextQObj = questions[nextIndex];
      setCurrentQuestionObj(nextQObj);
      await speakAI(nextQObj.question);
      startContinuousListening();
    }
  }, [capturedAnswers, currentQuestionObj, handleSubmitHrVoiceAnswers, questionIndex, questions, speakAI, startContinuousListening, stopListening, applicantInfo]);

  handleCandidateFinishedSpeakingRef.current = handleCandidateFinishedSpeaking;

  // Start interview trigger
  const handleStartHrInterview = async () => {
    setError(null);
    setScreen('interview');

    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      const loc = applicantInfo?.location || 'our target office location';
      const pos = cleanPosition(applicantInfo?.position);

      const qList = questions.length > 0 ? questions : [
        { id: 'req_current_ctc', question: 'To begin our HR screening, what is your current annual CTC or salary in your present role?' },
        { id: 'req_expected_ctc', question: 'What is your expected annual CTC for this position?' },
        { id: 'req_location', question: `This position is based in ${loc}. Are you currently located there or open to relocating and working onsite?` },
        { id: 'req_experience', question: `How many years of relevant work experience do you have for this ${pos} position?` },
        { id: 'req_notice', question: 'What is your current notice period at your organization?' },
        { id: 'req_status', question: 'What is your current employment status — for example, currently employed, serving notice, or available immediately?' },
        { id: 'req_availability', question: 'If selected for this position, how soon would you be available to join our team?' }
      ];

      setQuestions(qList);
      setQuestionIndex(0);
      setCurrentQuestionObj(qList[0]);
      setCapturedAnswers({});

      await speakAI(qList[0].question);
      startContinuousListening();
    } catch (err) {
      console.error('Failed to start HR Voice Interview:', err);
      setError(err.message || 'Microphone access failed.');
      setScreen('intro');
    }
  };

  useEffect(() => {
    return () => {
      stopListening();
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [stopListening]);

  if (!isOpen) return null;

  const candidateName = applicantInfo?.applicant_name || 'Candidate';
  const jobPosition = cleanPosition(applicantInfo?.position || 'Job Position');
  const companyName = applicantInfo?.company || 'InChi Solutions';
  const maxQ = Math.max(1, questions.length);
  const progressPct = Math.round((Math.min(questionIndex + 1, maxQ) / maxQ) * 100);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl text-slate-100 relative flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900/95 backdrop-blur z-20">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold">
              <Icon name="ClipboardCheck" size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-wide flex items-center gap-2">
                <span>HR Screening Voice Interview</span>
                <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[10px] font-bold uppercase tracking-wider">
                  Logistics & Fit
                </span>
              </h2>
              <p className="text-xs text-slate-400">Position: {jobPosition} &bull; {companyName}</p>
            </div>
          </div>
          <button
            onClick={() => {
              stopListening();
              if (window.speechSynthesis) window.speechSynthesis.cancel();
              onClose();
            }}
            className="w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 flex flex-col justify-center">
          {/* SCREEN 1: INTRO */}
          {screen === 'intro' && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center border border-blue-500/30 shadow-lg shadow-blue-500/10">
                  <Icon name="UserCheck" size={32} />
                </div>
                <h3 className="text-xl font-extrabold text-white">AI Voice Screening Passed!</h3>
                <p className="text-sm text-slate-300 max-w-md mx-auto">
                  Welcome to your <strong>HR Screening Voice Interview</strong>. Please answer 7 quick voice questions regarding your experience, location, CTC, notice period, and availability.
                </p>
              </div>

              {/* Details */}
              <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between items-center py-1 border-b border-slate-700/60">
                  <span className="text-slate-400">Candidate:</span>
                  <span className="font-semibold text-white">{candidateName}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-700/60">
                  <span className="text-slate-400">Position:</span>
                  <span className="font-semibold text-white">{jobPosition}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-400">Company:</span>
                  <span className="font-semibold text-white">{companyName}</span>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-xs flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  {error}
                </div>
              )}

              <div className="pt-2 space-y-3">
                <Button
                  variant="default"
                  size="lg"
                  className="w-full py-4 text-base font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-xl shadow-blue-500/20 flex items-center justify-center gap-2"
                  onClick={handleStartHrInterview}
                >
                  <Icon name="Mic" size={20} />
                  <span>START HR VOICE SCREENING</span>
                </Button>

                <div className="text-center">
                  <button
                    onClick={() => {
                      onClose();
                      window.open(`/hr-screening/${applicationId}`, '_blank');
                    }}
                    className="text-xs text-slate-400 hover:text-blue-400 underline transition-colors"
                  >
                    Prefer typing? Switch to Text Questionnaire
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SCREEN 2: INTERVIEW */}
          {screen === 'interview' && (
            <div className="space-y-6 animate-fade-in text-center">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-semibold text-slate-400">
                  <span>HR Logistics Question {questionIndex + 1} of {maxQ}</span>
                  <span>{progressPct}% Complete</span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden border border-slate-700/60">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
                <span>
                  {voiceStatus === 'ai_speaking' && 'HR AI IS SPEAKING...'}
                  {voiceStatus === 'listening' && 'YOUR SPOKEN VOICE (LIVE TRANSCRIPT)'}
                  {voiceStatus === 'processing_answer' && 'SAVING SPOKEN RESPONSE...'}
                  {voiceStatus === 'starting' && 'INITIALIZING HR SCREENING...'}
                </span>
              </div>

              {/* Question Display */}
              <div className="bg-slate-800/90 border border-slate-700 rounded-2xl p-6 shadow-inner space-y-3">
                <p className="text-xs uppercase tracking-wider text-blue-400 font-bold">HR Screening Question {questionIndex + 1}</p>
                <h4 className="text-lg sm:text-xl font-bold text-white leading-snug">
                  {currentQuestionObj?.question || "Loading HR question..."}
                </h4>
              </div>

              {/* Soundwave Animation */}
              <ModalVoiceSoundwave isActive={voiceStatus === 'ai_speaking' || voiceStatus === 'listening'} color="indigo" />

              {/* Spoken Response Box */}
              <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 min-h-[90px] flex flex-col justify-between text-left relative">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Live Candidate Transcript</p>
                  <p className="text-sm text-slate-200 italic font-mono leading-relaxed">
                    {liveTranscript ? `"${liveTranscript}"` : (voiceStatus === 'listening' ? 'Listening to your voice answer...' : 'Waiting...')}
                  </p>
                </div>
                {voiceStatus === 'listening' && (
                  <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping" />
                    <span>Pause speaking for 3.0s to auto-advance to next question</span>
                  </p>
                )}
              </div>

              {/* Skip / Manual Advance */}
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs"
                  onClick={() => {
                    stopListening();
                    handleCandidateFinishedSpeaking();
                  }}
                >
                  <span>Submit & Next Question</span>
                  <Icon name="ArrowRight" size={14} />
                </Button>
              </div>
            </div>
          )}

          {/* SCREEN 3: EVALUATING */}
          {screen === 'evaluating' && (
            <div className="space-y-6 text-center py-8 animate-fade-in">
              <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <div className="relative w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-400 flex items-center justify-center">
                  <Icon name="Cpu" size={32} />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white">Evaluating Requirement Match Score</h3>
                <p className="text-sm text-slate-400 max-w-sm mx-auto">
                  Our AI engine is matching your spoken responses against position requirements (CTC, location, experience, notice period)...
                </p>
              </div>
            </div>
          )}

          {/* SCREEN 4: COMPLETED */}
          {screen === 'completed' && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center space-y-2">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center border ${
                  result?.is_passed ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                }`}>
                  <Icon name={result?.is_passed ? "CheckCircle" : "Clock"} size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">
                  {result?.is_passed ? "HR Screening Passed — Auto-Advanced!" : "HR Screening Submitted"}
                </h3>
                <p className="text-sm text-slate-300 max-w-md mx-auto">
                  {result?.is_passed
                    ? "Your responses satisfied position requirements (≥70% match score). Your application has automatically advanced to the Shortlisted stage."
                    : "Your responses have been recorded and sent to the HR team for review."}
                </p>
              </div>

              {/* Match Score Display */}
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-5 space-y-4 text-left">
                <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                  <span className="text-sm font-semibold text-slate-300">Requirement Match Score:</span>
                  <span className={`text-2xl font-extrabold font-mono ${result?.is_passed ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {result?.requirement_match_score || 0}%
                  </span>
                </div>
                {result?.summary && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Evaluation Summary:</p>
                    <p className="text-sm text-slate-200 leading-relaxed">{result.summary}</p>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <Button
                  variant="default"
                  size="lg"
                  className="w-full py-4 text-base font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-xl shadow-blue-500/20 flex items-center justify-center gap-2 cursor-pointer"
                  onClick={() => {
                    onClose();
                    if (onCompleted) onCompleted(result);
                  }}
                >
                  <span>Return to Job Marketplace</span>
                </Button>
              </div>
            </div>
          )}

          {/* SCREEN 5: EXPIRED */}
          {screen === 'expired' && (
            <div className="space-y-5 animate-fade-in text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 text-red-400 flex items-center justify-center border border-red-500/30">
                <Icon name="AlertTriangle" size={36} />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-white">HR Screening Window Expired</h3>
                <p className="text-sm text-slate-400 max-w-md mx-auto">
                  The deadline to complete your HR Voice Screening has passed.
                </p>
              </div>
              <Button
                variant="outline"
                size="md"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateHrVoiceScreeningModal;
